/**
 * DossierSummaryPure
 *
 * The single dossier view: a condensed research summary — the background blurb
 * plus talking points (the parts you act on), with the rest tucked behind
 * "More". Header carries an "Updated Xd ago · Stale" badge and a Regenerate
 * (force-refresh) button.
 *
 * Shared pure component — rendered both in the Take Action panel
 * (TakeActionPanelPure) and in the contact-detail action tab's quick actions
 * (ContactOneOffActionSectionPure). All data in via props, all effects out via
 * callbacks, so every surface (dashboard, electron, extension) renders the same
 * summary. The `onUseText` / `onDraftFrom` composer seams are optional — surfaces
 * without a single always-open composer (e.g. the contact-detail quick actions)
 * simply omit them and no action buttons render.
 */
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { ContactDossier } from './types';

import { Button } from '../Input2';
import { Arrows, General } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import { cn } from '../cn';

export function DossierSummaryPure({
  dossier,
  generating,
  onGenerateDossier,
  isGeneratingDossier,
  onUseText,
  onDraftFrom,
  draftingSeed,
  className,
}: {
  dossier: ContactDossier | null;
  /** True while the research worker is still building the dossier. */
  generating?: boolean;
  /** Manually kick off (or retry) dossier generation. `undefined` → no button shown. */
  onGenerateDossier?: () => void;
  /** True while the manual dossier-generate mutation is in flight (button spinner). */
  isGeneratingDossier?: boolean;
  /** Drop a dossier line straight into the composer (append). */
  onUseText?: (text: string, mode: 'replace' | 'append') => void;
  /** Draft a message seeded by a dossier line. */
  onDraftFrom?: (seed: string) => void;
  /** The dossier line currently being drafted (row spinner). */
  draftingSeed?: string | null;
  /** Override the outer container styling. Defaults to the Take Action panel's
   *  full-width bordered section; surfaces with their own padding (e.g. the
   *  contact-detail quick-actions scroll, which already has `p-4`) pass a
   *  card-style className instead to avoid double padding. */
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  // The dossier is "in progress" either because the research worker is building it
  // (generating) or because the rep just kicked off a manual generate (isGeneratingDossier).
  const inProgress = generating === true || isGeneratingDossier === true;

  return (
    <section
      className={className ?? 'border-b border-slate-100 px-4 py-3 dark:border-slate-800'}
      data-testid="dossier-summary">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Dossier
          </span>
          {inProgress && dossier == null && <WaveSpinner size={16} />}
        </div>
        {dossier != null && (
          <div className="flex shrink-0 items-center gap-2">
            <span
              className={cn(
                'text-2xs',
                dossier.isStale ? 'text-yellow-700 dark:text-yellow-400' : 'text-slate-400 dark:text-slate-500',
              )}
              data-testid="dossier-updated">
              Updated {formatUpdatedAgo(dossier.updatedAt)}
              {dossier.isStale && ' · Stale — will refresh on next campaign add'}
            </span>
            {onGenerateDossier && (
              <Button
                size="xs"
                color="blue"
                tone="outline"
                disabled={isGeneratingDossier}
                icon={isGeneratingDossier ? <WaveSpinner /> : <Arrows.RefreshCw04 />}
                tooltip="Force-refresh the dossier now"
                onClick={onGenerateDossier}
                data-testid="regenerate-dossier-button">
                Regenerate
              </Button>
            )}
          </div>
        )}
      </div>

      {dossier == null && inProgress && <DossierSkeleton />}
      {dossier == null && !inProgress && (
        <div className="mt-1 flex flex-col items-start gap-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            No dossier yet{onGenerateDossier ? '.' : " — it's generated when this contact is added to a campaign."}
          </p>
          {onGenerateDossier && (
            <Button
              size="xs"
              color="blue"
              tone="soft"
              icon={<General.Zap />}
              onClick={onGenerateDossier}
              data-testid="generate-dossier-button">
              Generate dossier
            </Button>
          )}
        </div>
      )}

      {dossier != null && (
        <>
          {dossier.background !== '' && (
            <p className="mt-2 whitespace-pre-line text-sm text-slate-700 dark:text-slate-300">{dossier.background}</p>
          )}
          <DossierBullets
            title="Talking points"
            items={dossier.talkingPoints}
            icon={<General.Zap />}
            emphasizeFirst
            onUseText={onUseText}
            onDraftFrom={onDraftFrom}
            draftingSeed={draftingSeed}
          />
          {expanded && (
            <>
              <DossierBullets title="Recent signals" items={dossier.recentSignals} onDraftFrom={onDraftFrom} draftingSeed={draftingSeed} />
              <DossierBullets title="Wealth signals" items={dossier.wealthSignals} onDraftFrom={onDraftFrom} draftingSeed={draftingSeed} />
              <DossierBullets title="Career highlights" items={dossier.careerHighlights} />
            </>
          )}
          {hasMoreDossier(dossier) && (
            <button
              type="button"
              onClick={() => setExpanded((p) => !p)}
              className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
              <Arrows.ChevronDown className={cn('size-3 transition-transform', expanded && 'rotate-180')} />
              {expanded ? 'Show less' : 'More'}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function hasMoreDossier(d: ContactDossier): boolean {
  return d.recentSignals.length > 0 || d.wealthSignals.length > 0 || d.careerHighlights.length > 0;
}

/** A shimmer placeholder line for the dossier skeleton. */
function SkeletonLine({ className }: { className?: string }) {
  return <span className={cn('block h-3 animate-pulse rounded bg-slate-200 dark:bg-slate-700', className)} />;
}

/**
 * The research pipeline narrated step by step ("reticulating splines" style) —
 * mirrors what the dossier worker actually does: profile read → web search →
 * firm sizing → signal mining → synthesis. Held on the last line if generation
 * runs long, since we can't know the real progress from here.
 */
const GENERATION_STATUS_LINES = [
  'Reading their LinkedIn profile…',
  'Scanning the web for recent news…',
  'Sizing up the firm — AUM, team, niche…',
  'Digging for wealth & career signals…',
  'Distilling it into talking points…',
  'Picking the strongest opener…',
];

const GENERATION_STATUS_STEP_MS = 1400;

/** Cycles through the research steps, advancing every ~1.4s and holding on the
 *  final line. Keyed re-mount per line gives a gentle fade-in per step. */
function CyclingGenerationStatus() {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIndex((prev) => {
        if (prev >= GENERATION_STATUS_LINES.length - 1) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, GENERATION_STATUS_STEP_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <style>{`@keyframes dossier-status-fade { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: none; } }`}</style>
      <p
        key={stepIndex}
        className="mt-1 text-xs text-slate-500 dark:text-slate-400"
        style={{ animation: 'dossier-status-fade 0.4s ease-in' }}
        data-testid="dossier-status-line">
        {GENERATION_STATUS_LINES[stepIndex]}
      </p>
    </>
  );
}

/**
 * Loading state that previews the dossier's real shape — the background blurb,
 * the "Lead with this" card, and the talking-points list — so the rep knows
 * exactly what research is on its way instead of staring at a spinner.
 */
function DossierSkeleton() {
  return (
    <div data-testid="dossier-skeleton">
      <CyclingGenerationStatus />

      {/* Background blurb */}
      <div className="mt-2 flex flex-col gap-1.5">
        <SkeletonLine className="w-full" />
        <SkeletonLine className="w-4/5" />
      </div>

      {/* Talking points — real section header so the upcoming content is legible */}
      <div className="mt-2.5">
        <div className="flex items-center gap-1 text-2xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 [&>svg]:size-3">
          <General.Zap />
          Talking points
        </div>
        <div className="mt-1.5 rounded-lg border border-blue-100 bg-blue-50/40 p-3 dark:border-blue-900/40 dark:bg-blue-950/20">
          <div className="text-3xs font-semibold uppercase tracking-wider text-blue-400 dark:text-blue-500/70">
            Lead with this
          </div>
          <div className="mt-1.5 flex flex-col gap-1.5">
            <SkeletonLine className="w-full" />
            <SkeletonLine className="w-2/3" />
          </div>
        </div>
        <div className="mt-2 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span aria-hidden className="select-none text-slate-300 dark:text-slate-600">
              •
            </span>
            <SkeletonLine className="w-3/4" />
          </div>
          <div className="flex items-center gap-2">
            <span aria-hidden className="select-none text-slate-300 dark:text-slate-600">
              •
            </span>
            <SkeletonLine className="w-1/2" />
          </div>
        </div>
      </div>

      {/* What else is coming */}
      <p className="mt-2.5 text-2xs text-slate-400 dark:text-slate-500">
        Plus recent signals, wealth signals &amp; career highlights
      </p>
    </div>
  );
}

/**
 * Human-readable "3 months ago" / "yesterday" string from an ISO timestamp.
 * Past-only (dossier `updatedAt` is always in the past). Kept local rather than
 * pulled from luxon because this component renders on Electron/extension
 * surfaces too and we don't want the date-lib coupling.
 */
function formatUpdatedAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'recently';
  const diffMs = Date.now() - then;
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  const diffMo = Math.round(diffDay / 30);
  if (diffMo < 12) return `${diffMo} month${diffMo === 1 ? '' : 's'} ago`;
  const diffYr = Math.round(diffMo / 12);
  return `${diffYr} year${diffYr === 1 ? '' : 's'} ago`;
}

function DossierBullets({
  title,
  items,
  icon,
  emphasizeFirst = false,
  onUseText,
  onDraftFrom,
  draftingSeed,
}: {
  title: string;
  items: string[];
  icon?: ReactNode;
  /** When true, the first item leads in a prominent card ("lead with this")
   *  so the rep's eye lands on the single strongest thing to say. */
  emphasizeFirst?: boolean;
  /** When set, the lead card gets a "Use this" button (append to composer). */
  onUseText?: (text: string, mode: 'replace' | 'append') => void;
  /** When set, every row gets a "Draft" button seeded by that line. */
  onDraftFrom?: (seed: string) => void;
  /** The row text currently being drafted (spinner on that Draft button). */
  draftingSeed?: string | null;
}) {
  if (items.length === 0) return null;
  const [lead, ...rest] = emphasizeFirst ? items : [];
  const plain = emphasizeFirst ? rest : items;
  const hasRowActions = onDraftFrom != null;

  return (
    <div className="mt-2.5">
      <div className="flex items-center gap-1 text-2xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 [&>svg]:size-3">
        {icon}
        {title}
      </div>

      {/* Lead item — a real card: roomy, readable, with actions. The single
          most useful thing on the panel, so give it space. */}
      {emphasizeFirst && lead != null && (
        <div className="mt-1.5 rounded-lg border border-blue-200 bg-blue-50/70 p-3 dark:border-blue-800/60 dark:bg-blue-950/30">
          <div className="text-3xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
            Lead with this
          </div>
          <p className="mt-1 text-sm leading-relaxed text-slate-800 dark:text-slate-100">{lead}</p>
          {(onUseText != null || onDraftFrom != null) && (
            <div className="mt-2 flex items-center gap-2">
              {onUseText != null && (
                <Button size="2xs" color="blue" onClick={() => onUseText(lead, 'append')}>
                  Use this
                </Button>
              )}
              {onDraftFrom != null && (
                <Button
                  size="2xs"
                  color="blue"
                  tone="outline"
                  disabled={draftingSeed === lead}
                  icon={draftingSeed === lead ? <WaveSpinner size={10} /> : undefined}
                  onClick={() => onDraftFrom(lead)}>
                  Draft from this
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {plain.length > 0 && (
        <ul className={cn('flex flex-col gap-1 text-sm text-slate-700 dark:text-slate-300', emphasizeFirst ? 'mt-2' : 'mt-1')}>
          {plain.map((line, idx) => (
            <li key={idx} className="group flex items-start gap-2">
              <span aria-hidden className="select-none text-slate-400 dark:text-slate-500">
                •
              </span>
              <span className="min-w-0 flex-1">{line}</span>
              {hasRowActions && (
                <Button
                  size="2xs"
                  color="blue"
                  tone="soft"
                  disabled={draftingSeed === line}
                  icon={draftingSeed === line ? <WaveSpinner size={10} /> : undefined}
                  className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                  onClick={() => onDraftFrom(line)}>
                  Draft
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
